import React from 'react';
import { proxy, useSnapshot } from 'valtio';
import z from 'zod';

export const BooleanCoercionWithDefault = (defaultValue: boolean) =>
  z.union([z.string(), z.number(), z.boolean(), z.null(), z.undefined()]).transform((val) => {
    if (typeof val === 'boolean') return val;

    if (typeof val === 'string') {
      if (val === 'true' || val === '1') return true;
      if (val === 'false' || val === '0') return false;
    }

    if (typeof val === 'number') {
      if (val === 1) return true;
      if (val === 0) return false;
    }

    return defaultValue;
  });

export const ConfigSchema = z.object({
  /** Adobe PDF Embed API client ID, used to render PDFs in the UI. */
  ADOBE_SDK_KEY: z.string().default(''),
  /** Adobe PDF Embed API client ID bound to localhost. Only served in non-prod environments. */
  ADOBE_SDK_KEY_LOCALHOST: z.string().default(''),
  /** Base URL of the backend API. */
  API_URL: z.string().default(''),
  /** Cognito user-pool app client ID for standard authentication. */
  APP_CLIENT_ID: z.string().default(''),
  /** Cognito user-pool app client ID for restricted/guest-access sessions. */
  APP_CLIENT_ID2: z.string().default(''),
  /** Cognito authentication flow type used by Amplify Auth. */
  APP_FLOW: z.string().default(''),
  /** AWS region passed to Amplify Auth configuration. */
  APP_REGION: z.string().default(''),
  /** Cognito user pool ID passed to Amplify Auth configuration. */
  APP_USER_POOL_ID: z.string().default(''),
  /** Frontend version label shown in the UI. */
  APP_VERSION: z.string().default(''),
  /** Feature flag enabling the Cognito-based login UI. */
  COGNITO: BooleanCoercionWithDefault(true),
  /** External URL for the 'Contact us' link on the login screen. */
  CONTACT_US_URL: z.string().default(''),
  /** Backend/database version label shown in the UI footer. */
  DATABASE_VERSION: z.string().default(''),
  /** Disables exponential-backoff retries on failed API calls. */
  DISABLED_EXPONENTIAL_BACKOFF: BooleanCoercionWithDefault(false),
  /** Google Analytics measurement ID used to initialize tracking. */
  GOOGLE_ANALYTICS_TAG: z.string().default(''),
  /** Enables the guest-access link to resend flow on the restricted access view. */
  GUEST_ACCESS_RESEND: BooleanCoercionWithDefault(false),
  /** SSO/identity-provider configuration: OAuth domain, redirect URLs, and the providers shown on the login screen. */
  IDENTITY_PROVIDERS: z
    .object({
      domain: z.string().nullable().default(''),
      redirectSignIn: z.string().nullable().default(''),
      redirectSignOut: z.string().nullable().default(''),
      providers: z
        .array(
          z.object({
            label: z.string().nullable().default(''),
            message: z.string().nullable().default(''),
            provider: z.string().nullable().default('')
          })
        )
        .default([])
    })
    .nullable()
    .default({}),
  /** LogRocket app ID; when set, LogRocket session replay is initialized. */
  LOG_ROCKET_APP_ID: z.string().nullable().default(''),
  /** Masks text input values in LogRocket session recordings to protect sensitive data. */
  LOG_ROCKET_INPUT_SANITIZER: BooleanCoercionWithDefault(false),
  /** Shows a native browser alert on logout events. */
  LOGOUT_ALERT: BooleanCoercionWithDefault(false),
  /** Environment identifier. */
  LUCY_ENV: z.string().default('ndev'),
  /** Enables the CXEngage live chat widget for patient users. */
  LIVE_CHAT: BooleanCoercionWithDefault(false),
  /** Length (number of digits) of the MFA code. */
  MFA_LENGTH: z.coerce.number().default(6),
  /** Minimum seconds between MFA code requests (resend cooldown). */
  MFA_SECS: z.string().default(''),
  /** Validity period of an MFA code in seconds. */
  MFA_VALIDITY_SECS: z.string().default(''),
  /** Twilio latency threshold below which a connectivity warning is shown. */
  NETWORK_LATENCY_WARNING_THRESHOLD: z.string().default(''),
  /** Twilio quality threshold below which a connectivity warning is shown. */
  NETWORK_QUALITY_WARNING_THRESHOLD: z.string().default(''),
  /** Toggles NHS-specific UI and registration flows. */
  NHS_CLIENT: BooleanCoercionWithDefault(false),
  /** Runtime environment label used to gate FE dev tooling (e.g., test-failure injection). */
  NODE_ENV: z.enum(['development', 'production', 'test']).default('development'),
  /** URL of the production patient app, shown as a link on non-prod login screens. */
  PROD_PATIENT_URL: z.string().nullable().default(null),
  /** Selects the video provider used for video calls ('twilio' or 'chime'). */
  PVIDEO: z.enum(['twilio', 'chime']).default('twilio'),
  /** Enables Progressive Web App features on iOS. */
  PWA_FEATURE_FLAG: BooleanCoercionWithDefault(false),
  /** Minimum iOS version required for Progressive Web App features to be enabled. */
  PWA_MINIMUM_IOS_VERSION: z.string().default(''),
  /** Client ID used during the patient self-referral registration flow. */
  SELF_REFER_CLIENT_ID: z.number().default(0),
  /** Enables the self-registration link/flow on the login screen. */
  SELF_REG: BooleanCoercionWithDefault(false),
  /** Identity-provider key used for self-registration via SSO; must match a value in IDENTITY_PROVIDERS. */
  SELF_REG_PROVIDER: z.string().default(''),
  /** CXEngage (Serenova) tenant ID used in contact-management API URLs. */
  SERENOVA_TENANT: z.string().default(''),
  /** Uses the server locale for date formatting; otherwise the browser locale. */
  SERVER_LOCALE: BooleanCoercionWithDefault(false),
  /** Maximum number of characters allowed in a short task note input. */
  SHORT_TASK_NOTES_LIMIT: z.coerce.number().default(50),
  /** Stripe publishable key used to initialize Stripe.js for payments. Empty value disables Stripe. Marked for removal — Stripe is being phased out. */
  //STRIPE_PUBLISHABLE_KEY: z.string().default(''),
  /** SurveyJS license key applied to the survey viewer. */
  SURVEYJS_LICENSE_KEY: z.string().default(''),
  /** Operational mode of the system; value 'bvt' triggers the maintenance UI. */
  SYSTEM_MODE: z.enum(['dev', 'production', 'bvt']).default('dev'),
  /** Dev-only random failure injection: comma/semicolon-separated 'path=probability' pairs. */
  TEST_RANDOM_FAILURES: z.string().default(''),
  /** Tenant theme key used to load the initial UI theme. */
  THEME: z.string().default(''),
  /** Enables toast notifications globally. */
  TOAST_MESSAGES: BooleanCoercionWithDefault(true),
  /** WebSocket URL of the Twilio backend used for real-time appointment events. */
  TWILIO_BACKEND_URL: z.string().default(''),
  /** Enables sending tab visibility-change events to the Twilio backend during appointment wait. */
  TWILIO_EVENT_VISIBILITY: BooleanCoercionWithDefault(false),
  /** Selects the onboarding-tour provider; 'STONLY' enables the Stonly integration, 'X' is a no-op placeholder. */
  TOUR_PROVIDER: z
    .union([z.literal('STONLY'), z.literal('X')])
    .nullable()
    .default(null),
  /** VAPID public key used to subscribe the browser to Web Push notifications. */
  VAPID_PUBLIC: z.string().default(''),
  /** Disables the patient welcome popup on the home page when true. */
  DISABLE_WELCOME_POPUP: BooleanCoercionWithDefault(false),
  /** Polling interval in seconds for refreshing the clinician's appointment list. */
  CLINICIAN_APPOINTMENT_REFRESH_INTERVAL_SECS: z.number().optional(),
  /** Minutes after appointment end during which the user can still rejoin the video call. */
  VIDEO_NO_CASTART_AFTER: z.coerce.number().default(0),
  /** Allows client-admins to edit referral specifications. */
  CLIENTADMIN_REFERRAL_SPECS: BooleanCoercionWithDefault(true),
  /** Extra minutes before appointment start during which the user can join the call. */
  VIDEO_START_BUFFER: z.coerce.number().optional(),
  /** Enables UBRN (Unique Booking Reference Number) features used by some NHS trusts to identify bookings. */
  UBRN_ENABLED: BooleanCoercionWithDefault(false),
  /** Gates rendering of the PIFU navigation tab in the admin portal. */
  PIFU_ENABLED: BooleanCoercionWithDefault(false),
  /** Clinician session UI version: 1 = legacy, 2 = current. */
  APPT_SESSION_VERSION: z.union([z.literal(1), z.literal(2)]).default(2),
  /** Maximum size in MB allowed for a generated survey PDF before upload is rejected. */
  MAX_SURVEY_SIZE_MB: z.number().default(18),
  /** Login flow version: 1 = legacy form-based, 2 = SSO-only. */
  LOGIN_VERSION: z.union([z.literal(1), z.literal(2)]).default(2),
  /** Enables Docman integration (UK NHS GP document exchange) across registration and clinician flows. */
  DOCMAN: BooleanCoercionWithDefault(true),
  /** Enables the clinician to manage docman consent */
  CLINDMCONSENT: BooleanCoercionWithDefault(false),
  /** Enables a 'Test video call' button in the patient header for pre-session audio/video diagnostics. */
  TEST_VIDEO_CALL_BUTTON: BooleanCoercionWithDefault(false),
  /** Enables a 'Test video call' button in the clinician calendar for pre-session audio/video diagnostics. */
  CLIN_TEST_VIDEO_CALL_BUTTON: BooleanCoercionWithDefault(false),
  /** Audio configuration for voice calling: WebRTC ICE transport policy and (currently unused) ringtone. */
  AUDIO_SETTINGS: z
    .object({
      ringtone: z.string(),
      rtcConfiguration: z.object({
        iceTransportPolicy: z.enum(['relay', 'all'])
      })
    })
    .optional(),
  /** Activates Cognito disaster-recovery mode: shows a warning banner and emails a temporary password instead of normal login. */
  COGDR: BooleanCoercionWithDefault(false),
  /** AWS WAF CAPTCHA API key used to render the captcha widget. */
  CF_KEY: z.string().default(''),
  /** Enables an animated microphone icon that visualizes live audio input level. */
  MIC_ACTIVITY_INDICATOR: BooleanCoercionWithDefault(false),
  /** Disables the Heidi AI feature globally. */
  DISABLE_HEIDI_AI: BooleanCoercionWithDefault(false),
  /** Minutes before appointment end at which to show the 'time running out' warning notification. */
  APPT_END_WARNING_MINS: z.coerce.number().default(5),
  /** User type based on subdomain (not authenticated user) */
  PORTAL_USER_TYPE_CODE: z.string().default(''),
  /** Enables backgrounds to be display in video calls */
  VIDEO_BACKGROUNDS_ENABLED: BooleanCoercionWithDefault(true)
});

export type Config = z.infer<typeof ConfigSchema>;

const blankConfig = { LUCY_ENV: 'ndev' } as Config;

export const RUNTIME_CONFIG_STATE = proxy({ runtimeConfig: blankConfig });

export function setRuntimeConfig(config: Config) {
  RUNTIME_CONFIG_STATE.runtimeConfig = config;
}

export function useRuntimeConfig() {
  return useSnapshot(RUNTIME_CONFIG_STATE).runtimeConfig;
}

// Emulate NextJS's getRuntimeConfig function
export function getRuntimeConfig() {
  return window.config || RUNTIME_CONFIG_STATE.runtimeConfig;
}

const ConfigContext = React.createContext(blankConfig);
export default ConfigContext;
export { ConfigContext };
